import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Theme } from '@mui/material';
import { FormProvider, Resolver, useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { makeStyles } from 'tss-react/mui';
import SaveIcon from '@mui/icons-material/Save';
import ErrorIcon from '@mui/icons-material/Error';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';

import FormSubmitButton from '../../components/buttons/FormSubmitButton';
import FormCancelButton from '../../components/buttons/FormCancelButton';
import FormTextField from '../../components/formFields/FormTextField';
import FormSelectField from '../../components/formFields/FormSelectField';
import AddressAutocomplete from '../../components/formFields/AddressAutocomplete';
import Loader from '../../components/Loader';
import VendorConfirmationPopup, { VendorOption } from '../../components/VendorConfirmationPopup';

import { QuickBooksVendorFormSchema } from '../../helpers/formSchemas/formSchemas';
import { US_STATES_LIST } from '../../constants/constants';
import { AddressPrediction, validateAddress } from '../../api/addressApi/addressApi';
import { addressOptionsQuery, mapAddressResults, NATIVE_ENTITY_TYPE_MAP } from './helpers';
import useSnackBar from '../../hooks/useSnackBar';
import { createQuickBooksVendor, getAccountingVendor } from '../../api/paymentsApi/settlementsApi';
import { getVendorAccount } from '../../api/vendorApi/vendorAccountsApi';
import { getClientRecord } from '../../api/clientsApi/clientsApi';
import { getFirmRecord } from '../../api/firmsApi/firmsApi';
import { AccountingVendor, AccountingVendorField, ErrorData, VendorAccountDetail } from '../../types';
import { AxiosError } from 'axios';

const useStyles = makeStyles()((theme: Theme) => ({
  vendorCreateForm: {
    padding: '32px',
    flexBasis: '896px',
    backgroundColor: theme.palette.primary.light,
    borderRadius: '16px',

    '@media (max-width: 1100px)': {
      flexBasis: 'auto',
    },

    '@media (max-width: 725px)': {
      flexBasis: '520px',
      padding: '16px',
    },
  },

  formContainer: {
    display: 'flex',
    width: '100%',
    gap: '32px',
    paddingBottom: '20px',

    '@media (max-width: 1100px)': {
      flexDirection: 'column',
    },
  },

  divider: {
    height: '1px',
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    margin: '0px',
  },

  fieldHeadline: {
    fontWeight: 600,
    fontSize: '20px',
    lineHeight: '24px',
    color: theme.palette.common.white,
    fontFamily: 'Inter',
  },

  formWrapper: {
    display: 'flex',
  },
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },

  verifyButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '14px',
    fontFamily: 'Inter',

    '&:hover': {
      opacity: 0.8,
    },

    padding: '7px 14px',
    borderRadius: '8px',
    backgroundColor: theme.palette.info.main,
  },
}));

type QuickBooksVendorFormValues = Record<string, string | number | boolean | undefined>;
const ADDRESS_FIELD_NAMES = ['address1', 'address2', 'city', 'state', 'zip', 'country'];
const EMPTY_ADDRESS_STATE = {
  address1: '',
  address2: '',
  city: '',
  state: '',
  zip: '',
};

type PrefillAddress = {
  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  isAddressVerified?: boolean;
};

type PrefillNativeEntity = {
  name?: string | null;
  email?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  address: PrefillAddress;
};

const QuickBooksCreateVendorPage = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const { setAlert } = useSnackBar();
  const location = useLocation();

  const entityType = Object.entries(NATIVE_ENTITY_TYPE_MAP).find(([, value]) => value === location.state?.entityType,
  )?.[0] ?? location.state?.entityType;
  const entityId = location.state?.entityId;

  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false);
  const [vendorConfirmationOpen, setVendorConfirmationOpen] = useState<boolean>(false);
  const [isManualVerification, setIsManualVerification] = useState<boolean>(false);

  const [accountingVendorConfig, setAccountingVendorConfig] =
    useState<AccountingVendor | null>(null);

  const [addressKey, setAddressKey] = useState<number>(0);

  const [addressFromAutocomplete, setAddressFromAutocomplete] = useState<boolean>(false);
  const [addressVerifiedByPopup, setAddressVerifiedByPopup] = useState<boolean>(false);
  const [addressKeptAsEntered, setAddressKeptAsEntered] = useState<boolean>(false);

  const [originalKeptAddress, setOriginalKeptAddress] = useState<string>('');

  const [lastAutoCompleteAddressState, setLastAutoCompleteAddressState] =
    useState(EMPTY_ADDRESS_STATE);

  const [isUpdatingFromAutocomplete, setIsUpdatingFromAutocomplete] = useState<boolean>(false);
  const [isVendorAccountLoading, setIsVendorAccountLoading] = useState<boolean>(false);

  const vendorFormSchema = useMemo(
    () => QuickBooksVendorFormSchema(accountingVendorConfig),
    [accountingVendorConfig],
  );

  const formMethods = useForm<QuickBooksVendorFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',

    resolver: yupResolver(vendorFormSchema) as Resolver<QuickBooksVendorFormValues>,

    defaultValues: {
      isAddressVerified: false,
      isAddressConfirmationCompleted: false,
    },
  });

  const {
    formState: { isValid, isDirty },
    handleSubmit,
    reset,
    setValue,
    clearErrors,
    trigger,
    getValues,
  } = formMethods;

  const commonFields = useMemo<AccountingVendorField[]>(
    () => accountingVendorConfig?.fields ?? [],
    [accountingVendorConfig],
  );

  const providerFields = useMemo<AccountingVendorField[]>(
    () => accountingVendorConfig?.providerFields ?? [],
    [accountingVendorConfig],
  );

  const configuredFieldNames = useMemo(
    () => new Set(commonFields.map((field) => field.name)),
    [commonFields],
  );

  const vendorFields = useMemo(
    () => commonFields.filter((field) => !ADDRESS_FIELD_NAMES.includes(field.name)),
    [commonFields],
  );

  const addressFields = useMemo(
    () => commonFields.filter((field) => ADDRESS_FIELD_NAMES.includes(field.name)),
    [commonFields],
  );

  const address1 = useWatch({ control: formMethods.control, name: 'address1', defaultValue: '' });
  const address2 = useWatch({ control: formMethods.control, name: 'address2', defaultValue: '' });
  const city = useWatch({ control: formMethods.control, name: 'city', defaultValue: '' });
  const state = useWatch({ control: formMethods.control, name: 'state', defaultValue: '' });
  const zip = useWatch({ control: formMethods.control, name: 'zip', defaultValue: '' });

  const getVendorDefaultValues = (config?: AccountingVendor | null): QuickBooksVendorFormValues => {
    const defaults: QuickBooksVendorFormValues = {
      isAddressVerified: false,
      isAddressConfirmationCompleted: false,
    };

    if (!config) {
      return defaults;
    }

    const fields = [...(config.fields ?? []), ...(config.providerFields ?? [])];

    fields.forEach((field) => {
      defaults[field.name] = field.type === 'boolean' ? false : '';
    });

    return defaults;
  };

  const getFieldValueFromNativeEntity = (fieldName: string, source: PrefillNativeEntity) => {
    const normalizedFieldName = fieldName.trim().toLowerCase();
    const canonicalFieldName = normalizedFieldName.replace(/[^a-z0-9]/g, '');

    const fullName = [source.firstName, source.lastName].filter(Boolean).join(' ').trim();

    if (canonicalFieldName === 'name') {
      return source.name || fullName;
    }

    if (canonicalFieldName === 'email') {
      return source.email ?? '';
    }

    if (canonicalFieldName === 'firstname') {
      return source.firstName ?? '';
    }

    if (canonicalFieldName === 'lastname') {
      return source.lastName ?? '';
    }

    if (canonicalFieldName === 'isaddressverified') {
      return source.address?.isAddressVerified ?? false;
    }

    if (canonicalFieldName === 'address1') {
      return source.address?.address1 ?? '';
    }

    if (canonicalFieldName === 'address2') {
      return source.address?.address2 ?? '';
    }

    if (canonicalFieldName === 'city') {
      return source.address?.city ?? '';
    }

    if (canonicalFieldName === 'state') {
      return source.address?.state ?? '';
    }

    if (canonicalFieldName === 'zip') {
      return source.address?.zip ?? '';
    }

    return undefined;
  };

  const buildVendorPrefillValues = (
    config: AccountingVendor,
    source: PrefillNativeEntity,
  ): QuickBooksVendorFormValues => {
    const values = getVendorDefaultValues(config);

    const fields = [...(config.fields ?? []), ...(config.providerFields ?? [])];

    fields.forEach((field) => {
      const resolvedValue = getFieldValueFromNativeEntity(field.name, source);

      if (resolvedValue === undefined || resolvedValue === null) {
        return;
      }

      if (field.type === 'boolean') {
        values[field.name] = Boolean(resolvedValue);
        return;
      }

      values[field.name] =
        typeof resolvedValue === 'number'
          ? resolvedValue
          : String(resolvedValue);
    });

    return values;
  };

  const normalizeEntityType = (
    value?: string,
  ): 'VENDOR' | 'FIRM' | 'CLIENT' | null => {
    switch (value) {
      case 'VENDOR_FIRM_PARTICIPATION':
        return 'VENDOR';

      case 'FIRM':
        return 'FIRM';

      case 'CLIENT':
        return 'CLIENT';

      default:
        return null;
    }
  };

  const mapVendorAccountToPrefillEntity = (vendorAccount: VendorAccountDetail): PrefillNativeEntity => ({
    name: vendorAccount.name,
    email: vendorAccount.email,
    address: {
      address1: vendorAccount.participationAddress?.address1,
      address2: vendorAccount.participationAddress?.address2,
      city: vendorAccount.participationAddress?.city,
      state: vendorAccount.participationAddress?.state,
      zip: vendorAccount.participationAddress?.zip,
      isAddressVerified: vendorAccount.participationAddress?.isAddressVerified,
    },
  });

  const mapClientToPrefillEntity = (clientRecord: {
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    countryState?: string | null;
    zip?: string | null;
  }): PrefillNativeEntity => ({
    firstName: clientRecord.firstName,
    lastName: clientRecord.lastName,
    email: clientRecord.email,
    address: {
      address1: clientRecord.address1,
      address2: clientRecord.address2,
      city: clientRecord.city,
      state: clientRecord.countryState,
      zip: clientRecord.zip,
      isAddressVerified: false,
    },
  });

  const mapFirmToPrefillEntity = (firmRecord: {
    name?: string | null;
    firstName?: string | null;
    lastName?: string | null;
    email?: string | null;
    address1?: string | null;
    address2?: string | null;
    city?: string | null;
    state?: string | null;
    zip?: string | null;
  }): PrefillNativeEntity => ({
    name: firmRecord.name,
    firstName: firmRecord.firstName,
    lastName: firmRecord.lastName,
    email: firmRecord.email,
    address: {
      address1: firmRecord.address1,
      address2: firmRecord.address2,
      city: firmRecord.city,
      state: firmRecord.state,
      zip: firmRecord.zip,
      isAddressVerified: false,
    },
  });

  // Load accounting vendor configuration and prefill vendor data from the native vendor.
  useEffect(() => {
    let isMounted = true;

    const loadQuickBooksVendorData = async () => {
      try {
        const configResponse = await getAccountingVendor();

        if (!isMounted || !configResponse?.data) {
          return;
        }

        const vendorConfig = configResponse.data as AccountingVendor;
        setAccountingVendorConfig(vendorConfig);
        let resetValues = getVendorDefaultValues(vendorConfig);
        const nativeEntityId = Number(entityId);

        if (Number.isFinite(nativeEntityId) && nativeEntityId > 0) {
          setIsVendorAccountLoading(true);

          try {
            const normalizedEntityType = normalizeEntityType(entityType);
            let prefillSource: PrefillNativeEntity | null = null;
            console.log('Normalized entity type:', normalizedEntityType);

            if (normalizedEntityType === 'CLIENT') {
              const clientResponse = await getClientRecord(nativeEntityId);
              prefillSource = mapClientToPrefillEntity(clientResponse.data);
            } else if (normalizedEntityType === 'FIRM') {
              const firmResponse = await getFirmRecord(String(nativeEntityId));
              prefillSource = mapFirmToPrefillEntity(firmResponse.data);
            } else {
              const vendorAccountResponse = await getVendorAccount(nativeEntityId);
              prefillSource = mapVendorAccountToPrefillEntity(vendorAccountResponse.data);
            }

            if (!isMounted) {
              return;
            }

            if (!prefillSource) {
              return;
            }

            resetValues = buildVendorPrefillValues(vendorConfig, prefillSource);

            const participationAddress = prefillSource.address;

            const hasAddress = Boolean(
              participationAddress?.address1 ||
              participationAddress?.address2 ||
              participationAddress?.city ||
              participationAddress?.state ||
              participationAddress?.zip,
            );

            // Prefilled verified address: save it as the last verified address so that editing removes verification and reverting restores it.
            if (participationAddress?.isAddressVerified && hasAddress) {
              setLastAutoCompleteAddressState({
                address1: participationAddress.address1 || '',
                address2: participationAddress.address2 || '',
                city: participationAddress.city || '',
                state: participationAddress.state || '',
                zip: participationAddress.zip || '',
              });

              setAddressFromAutocomplete(true);
              setAddressVerifiedByPopup(false);
              setAddressKeptAsEntered(false);
              setOriginalKeptAddress('');
            } else if (hasAddress) {
              // Prefilled address exists but is not verified. Do NOT put this into lastAutoCompleteAddressState.
              setLastAutoCompleteAddressState(EMPTY_ADDRESS_STATE);

              setAddressFromAutocomplete(false);
              setAddressVerifiedByPopup(false);
              setAddressKeptAsEntered(true);

              setOriginalKeptAddress(
                `${participationAddress?.address1 || ''}, ${participationAddress?.city || ''
                }, ${participationAddress?.state || ''} ${participationAddress?.zip || ''
                }`,
              );
            } else {
              setLastAutoCompleteAddressState(EMPTY_ADDRESS_STATE);

              setAddressFromAutocomplete(false);
              setAddressVerifiedByPopup(false);
              setAddressKeptAsEntered(false);
              setOriginalKeptAddress('');
            }
          } catch {
            setAlert((prev) => ({
              ...prev,
              message: 'Failed to prefill vendor data.',
              type: 'warning',
            }));
          } finally {
            if (isMounted) {
              setIsVendorAccountLoading(false);
            }
          }
        }

        reset(resetValues);

        setAddressKey((prev) => prev + 1);
      } catch {
        setAlert({
          message: 'Failed to load vendor data.',
          type: 'error',
        });
      }
    };

    void loadQuickBooksVendorData();

    return () => {
      isMounted = false;
    };
  }, [entityId, entityType, reset, setAlert]);

  // Sync address verification state into the form.
  // Keep-as-entered means the confirmation process is completed, but the address is still considered unverified.
  useEffect(() => {
    const confirmationCompleted =
      addressFromAutocomplete ||
      addressVerifiedByPopup ||
      addressKeptAsEntered;

    const isAddressVerified =
      addressFromAutocomplete || addressVerifiedByPopup;

    if (
      getValues('isAddressConfirmationCompleted') !==
      confirmationCompleted
    ) {
      setValue('isAddressConfirmationCompleted', confirmationCompleted, {
        shouldValidate: true,
        shouldDirty: true,
      });
    }

    if (getValues('isAddressVerified') !== isAddressVerified) {
      setValue('isAddressVerified', isAddressVerified, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }
  }, [
    addressFromAutocomplete,
    addressVerifiedByPopup,
    addressKeptAsEntered,
    getValues,
    setValue,
  ]);

  // Restore/reset verification based on address changes.
  // Address2 is intentionally excluded to match the existing vendor-address verification flow.
  useEffect(() => {
    if (addressFromAutocomplete && !lastAutoCompleteAddressState.address1) {
      return;
    }

    if (isUpdatingFromAutocomplete) {
      return;
    }

    if (!lastAutoCompleteAddressState.address1) {
      if (addressKeptAsEntered && originalKeptAddress) {
        const currentFullAddress =
          `${address1}, ${city}, ${state} ${zip}`;

        if (currentFullAddress !== originalKeptAddress) {
          setAddressKeptAsEntered(false);
          setOriginalKeptAddress('');
        }
      }

      return;
    }

    const isSameAsVerified =
      address1 === lastAutoCompleteAddressState.address1 &&
      city === lastAutoCompleteAddressState.city &&
      state === lastAutoCompleteAddressState.state &&
      zip === lastAutoCompleteAddressState.zip;

    // User changed the verified address and then changed it back to the original verified address.
    if (
      isSameAsVerified &&
      !(addressFromAutocomplete || addressVerifiedByPopup)
    ) {
      setAddressFromAutocomplete(true);
      setAddressVerifiedByPopup(false);
      setAddressKeptAsEntered(false);

      setValue('isAddressConfirmationCompleted', true, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('isAddressVerified', true, {
        shouldValidate: false,
        shouldDirty: true,
      });
    } else if (
      !isSameAsVerified &&
      (addressFromAutocomplete || addressVerifiedByPopup)
    ) {
      // Verified address has been manually modified.
      setAddressFromAutocomplete(false);
      setAddressVerifiedByPopup(false);
      setAddressKeptAsEntered(false);

      setValue('isAddressConfirmationCompleted', false, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('isAddressVerified', false, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    // If an address was kept as entered and the user modifies it afterward, confirmation is required again.
    if (addressKeptAsEntered && originalKeptAddress) {
      const currentFullAddress =
        `${address1}, ${city}, ${state} ${zip}`;

      if (currentFullAddress !== originalKeptAddress) {
        setAddressKeptAsEntered(false);
        setOriginalKeptAddress('');
      }
    }
  }, [
    address1,
    city,
    state,
    zip,
    lastAutoCompleteAddressState,
    addressFromAutocomplete,
    addressVerifiedByPopup,
    addressKeptAsEntered,
    isUpdatingFromAutocomplete,
    originalKeptAddress,
    setValue,
  ]);

  // Track manual edits to a previously verified address.
  // lastAutoCompleteAddressState is intentionally retained so reverting the values restores verification.
  useEffect(() => {
    if (isUpdatingFromAutocomplete) {
      return;
    }

    if (
      (addressFromAutocomplete || addressVerifiedByPopup) &&
      lastAutoCompleteAddressState.address1 &&
      (
        address1 !== lastAutoCompleteAddressState.address1 ||
        city !== lastAutoCompleteAddressState.city ||
        state !== lastAutoCompleteAddressState.state ||
        zip !== lastAutoCompleteAddressState.zip
      )
    ) {
      setAddressFromAutocomplete(false);
      setAddressVerifiedByPopup(false);
      setAddressKeptAsEntered(false);
      setOriginalKeptAddress('');
    }
  }, [
    address1,
    city,
    state,
    zip,
    addressFromAutocomplete,
    addressVerifiedByPopup,
    lastAutoCompleteAddressState,
    isUpdatingFromAutocomplete,
  ]);

  // Address selected from autocomplete / confirmation popup.
  const handleAddressSelect = async (address: AddressPrediction) => {
    if (!address) {
      return;
    }

    const addressText = address.description || '';

    if (!addressText) {
      setIsUpdatingFromAutocomplete(false);
      return;
    }

    if (address.placeId) {
      setIsUpdatingFromAutocomplete(true);
    }

    if (configuredFieldNames.has('address1')) {
      setValue('address1', addressText);
    }

    // Plain manually entered value.
    if (!address.placeId) {
      setAddressFromAutocomplete(false);
      setAddressVerifiedByPopup(false);
      setIsUpdatingFromAutocomplete(false);

      return;
    }

    try {
      const validatedAddress = await validateAddress(
        addressText,
        address.placeId,
      );

      if (!validatedAddress.data) {
        setAddressFromAutocomplete(false);

        return;
      }

      const {
        addressLine1,
        addressLine2,
        city: validatedCity,
        state: validatedState,
        zip: validatedZip,
      } = validatedAddress.data;

      if (configuredFieldNames.has('address1')) {
        setValue('address1', addressLine1 || '');
      }

      if (configuredFieldNames.has('address2')) {
        setValue('address2', addressLine2 || '');
      }

      if (configuredFieldNames.has('city')) {
        setValue('city', validatedCity || '');
      }

      if (configuredFieldNames.has('state')) {
        setValue('state', validatedState || '');
      }

      if (configuredFieldNames.has('zip')) {
        setValue('zip', validatedZip || '');
      }

      // Store only successfully validated addresses.
      setLastAutoCompleteAddressState({
        address1: addressLine1 || '',
        address2: addressLine2 || '',
        city: validatedCity || '',
        state: validatedState || '',
        zip: validatedZip || '',
      });

      // Validation succeeded, therefore this address can now be considered verified.
      setAddressFromAutocomplete(true);
      setAddressKeptAsEntered(false);
      setOriginalKeptAddress('');

      const fieldsToValidate = [
        'address1',
        'address2',
        'city',
        'state',
        'zip',
      ].filter((fieldName) => configuredFieldNames.has(fieldName));

      if (fieldsToValidate.length > 0) {
        clearErrors(fieldsToValidate);

        await trigger(fieldsToValidate);
      }
    } catch {
      // Address validation failed. Keep the entered address but don't mark the address as verified.
      if (configuredFieldNames.has('address1')) {
        setValue('address1', addressText);
      }

      setAddressFromAutocomplete(false);
      setAddressVerifiedByPopup(false);
    } finally {
      setIsUpdatingFromAutocomplete(false);
    }
  };

  const handleVerifyAddress = () => {
    if (!address1) {
      setAlert((prev) => ({
        ...prev,
        message: 'Please enter an address to verify.',
        type: 'warning',
      }));

      return;
    }

    setIsManualVerification(true);
    setVendorConfirmationOpen(true);
  };

  const checkForCustomAddresses = () => {
    // Don't run address confirmation if the accounting provider doesn't require address1.
    if (!configuredFieldNames.has('address1')) {
      return true;
    }

    const hasAnyAddressInput = [
      address1,
      address2,
      city,
      state,
      zip,
    ].some((value) => String(value ?? '').trim() !== '');

    const hasCustomAddress =
      hasAnyAddressInput &&
      !addressFromAutocomplete &&
      !addressVerifiedByPopup &&
      !addressKeptAsEntered;

    if (hasCustomAddress) {
      setIsManualVerification(false);
      setVendorConfirmationOpen(true);

      return false;
    }

    return true;
  };

  // Send only configured accounting-system fields.
  const processSubmission = (
    values: QuickBooksVendorFormValues,
  ) => {
    setIsFormSubmitting(true);

    const formFieldValues = (
      accountingVendorConfig?.fields ?? []
    ).reduce<
      Record<string, string | number | boolean | undefined>
    >((payload, field) => {
      payload[field.name] = values[field.name];

      return payload;
    }, {});

    const providerData = (
      accountingVendorConfig?.providerFields ?? []
    ).reduce<
      Record<string, string | number | boolean | undefined>
    >((payload, field) => {
      payload[field.name] = values[field.name];

      return payload;
    }, {});

    const payload = {
      accountingSystem: accountingVendorConfig?.accountingSystem,
      ...formFieldValues,
      providerData,
      nativeEntityId: entityId,
      nativeEntityType: entityType,
      accountingEntityType: 'VENDOR'
    };

    createQuickBooksVendor(payload)
      .then(() => {
        setAlert((prev) => ({
          ...prev,
          message: 'Vendor created successfully.',
          type: 'success',
        }));

        navigate('/vendors');
      })
      .catch((error: AxiosError<ErrorData>) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data.message || 'An error occurred while creating the vendor account.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsFormSubmitting(false);
      });
  };

  // Confirmation popup callback.
  const handleVendorConfirmation = (
    _selectedVendor?: VendorOption,
    keepAsEntered?: boolean,
  ) => {
    setVendorConfirmationOpen(false);

    if (keepAsEntered) {
      setAddressKeptAsEntered(true);
      setAddressFromAutocomplete(false);
      setAddressVerifiedByPopup(false);

      setOriginalKeptAddress(
        `${address1}, ${city}, ${state} ${zip}`,
      );

      setValue('isAddressConfirmationCompleted', true, {
        shouldValidate: true,
        shouldDirty: true,
      });

      setValue('isAddressVerified', false, {
        shouldValidate: false,
        shouldDirty: true,
      });
    }

    // If this confirmation happened during form submission, submit the CURRENT form values rather than a stale snapshot.
    if (!isManualVerification) {
      processSubmission(getValues());
    }
  };

  const handleVendorOptionSelect = async (
    vendor: VendorOption,
  ) => {
    const addressForValidation: AddressPrediction = {
      description: vendor.address,
      placeId: vendor.placeId,
      structuredFormatting: {
        mainText: vendor.address,
        secondaryText: '',
      },
    };

    // Popup verification is tracked separately for UI, while handleAddressSelect performs the actual address validation and updates the fields.
    setAddressVerifiedByPopup(true);
    setAddressKeptAsEntered(false);

    await handleAddressSelect(addressForValidation);
  };

  const onSubmitForm = (
    values: QuickBooksVendorFormValues,
  ) => {
    if (!checkForCustomAddresses()) {
      return;
    }

    processSubmission(values);
  };

  const onCancel = () => {
    setAddressKey((prev) => prev + 1);

    setAddressFromAutocomplete(false);
    setAddressVerifiedByPopup(false);
    setAddressKeptAsEntered(false);

    setOriginalKeptAddress('');

    setLastAutoCompleteAddressState(
      EMPTY_ADDRESS_STATE,
    );

    reset(
      getVendorDefaultValues(
        accountingVendorConfig,
      ),
    );
  };

  // Dynamic field rendering.
  // API decides: field name, label, required, whether the field exists.
  // UI decides which control renders the field.
  const renderField = (
    field: AccountingVendorField,
  ) => {
    const label = `${field.label}${field.required ? ' *' : ''}`;

    const normalizedFieldName =
      field.name.trim().toLowerCase();

    if (normalizedFieldName === 'address1') {
      return (
        <AddressAutocomplete
          key={addressKey}
          name={field.name}
          label={label}
          optionsQuery={addressOptionsQuery}
          mapQueryResults={mapAddressResults}
          onValueChange={handleAddressSelect}
          isDisableClearable={false}
          addressType="Vendor Address"
          InputProps={{
            value: address1,
            placeholder: `Enter ${field.label.toLowerCase()}`,
          }}
        />
      );
    }

    if (normalizedFieldName === 'state') {
      return (
        <FormSelectField
          name={field.name}
          label={label}
          options={US_STATES_LIST}
          width="100%"
          shouldClearable
        />
      );
    }

    return (
      <FormTextField
        name={field.name}
        label={label}
        InputProps={{
          placeholder: `Enter ${field.label.toLowerCase()}`,
        }}
      />
    );
  };

  if (!accountingVendorConfig) {
    return null;
  }

  if (isVendorAccountLoading) {
    return (
      <Box className={styles.classes.loaderContainer}>
        <Loader colorType="warning" size={70} />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ mb: 3 }} />

      <Box className={styles.classes.formWrapper}>
        <FormProvider {...formMethods}>
          <form className={styles.classes.vendorCreateForm}>
            <Box className={styles.classes.formContainer}>
              <Box
                display="flex"
                flexDirection="column"
                width="100%"
                maxWidth="470px"
              >
                {(vendorFields.length > 0 || providerFields.length > 0) && (
                  <>
                    <Typography className={styles.classes.fieldHeadline}>
                      Vendor Details
                    </Typography>

                    {vendorFields.map((field) => (
                      <Box key={field.name} mt="24px">
                        {renderField(field)}
                      </Box>
                    ))}

                    {providerFields.map((field) => (
                      <Box key={field.name} mt="24px">
                        {renderField(field)}
                      </Box>
                    ))}
                  </>
                )}
                {addressFields.length > 0 && (
                  <>
                    <Typography
                      className={
                        styles.classes.fieldHeadline
                      }
                      mt={
                        vendorFields.length > 0
                          ? '32px'
                          : '0'
                      }
                    >
                      Address
                    </Typography>

                    {configuredFieldNames.has(
                      'address1',
                    ) && (
                        <Box
                          mt="8px"
                          display="flex"
                          justifyContent="space-between"
                        >
                          {addressFromAutocomplete ||
                            addressVerifiedByPopup ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#4CAF50',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px',
                              }}
                            >
                              <CheckCircleIcon
                                sx={{
                                  fontSize: '16px',
                                }}
                              />

                              <span>
                                Admin side address verification
                                completed
                              </span>
                            </Box>
                          ) : addressKeptAsEntered ? (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                                color: '#FF9800',
                                fontSize: '14px',
                                fontWeight: 500,
                                fontFamily: 'Inter',
                                padding: '5px',
                              }}
                            >
                              <ErrorIcon
                                sx={{
                                  fontSize: '16px',
                                }}
                              />

                              <span>
                                Vendor address not verified by USPS
                              </span>
                            </Box>
                          ) : (
                            address1 &&
                            city &&
                            state &&
                            zip && (
                              <>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    color: '#FF9800',
                                    fontSize: '14px',
                                    fontWeight: 500,
                                    fontFamily: 'Inter',
                                    padding: '5px',
                                  }}
                                >
                                  <ErrorIcon
                                    sx={{
                                      fontSize: '16px',
                                    }}
                                  />

                                  <span>
                                    Vendor address not verified.
                                    Please verify
                                  </span>
                                </Box>

                                <Box
                                  onClick={
                                    handleVerifyAddress
                                  }
                                  className={
                                    styles.classes.verifyButton
                                  }
                                >
                                  <span>Verify</span>
                                </Box>
                              </>
                            )
                          )}
                        </Box>
                      )}

                    {addressFields.map(
                      (field, index) => (
                        <Box
                          key={field.name}
                          mt={
                            index === 0
                              ? '16px'
                              : '24px'
                          }
                        >
                          {renderField(field)}
                        </Box>
                      ),
                    )}
                  </>
                )}


              </Box>
            </Box>

            <Box
              className={styles.classes.divider}
            />

            <Box
              display="flex"
              width="100%"
              justifyContent="center"
            >
              <Box
                display="flex"
                mt="32px"
                maxWidth="416px"
                flexDirection="column"
                gap="16px"
                width="100%"
              >
                <FormSubmitButton
                  disabled={
                    !isValid ||
                    !isDirty ||
                    isFormSubmitting
                  }
                  buttonIcon={<SaveIcon />}
                  buttonText="Save"
                  onClick={handleSubmit(
                    onSubmitForm,
                  )}
                />

                <FormCancelButton
                  onClick={onCancel}
                />
              </Box>
            </Box>
          </form>
        </FormProvider>
      </Box>

      <VendorConfirmationPopup
        open={vendorConfirmationOpen}
        addressType="Vendor Address"
        enteredAddress={
          address1
            ? `${address1}${address2
              ? `, ${address2}`
              : ''
            }, ${city}, ${state} ${zip}`
            : ''
        }
        onConfirm={handleVendorConfirmation}
        onCancel={() =>
          setVendorConfirmationOpen(false)
        }
        onVendorSelect={
          handleVendorOptionSelect
        }
      />
    </>
  );
};

export default QuickBooksCreateVendorPage;